import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { dwellStart, dwellStop, funnel, startAnalytics, track } from "@/lib/analytics";

const SECTIONS = ["top", "insights", "how", "audience", "build"];
const SKIP = (path) => path.startsWith("/insights") || path.startsWith("/admin");

export default function AnalyticsTracker({ consent }) {
  const { pathname } = useLocation();

  useEffect(() => {
    if (consent !== "granted" || SKIP(pathname)) return undefined;
    startAnalytics();
    funnel("landed");
    track("pageview", "view", { path: pathname });

    const seen = new Set();
    const engagedTimer = setTimeout(() => funnel("engaged"), 15000);
    const onScroll = () => {
      const doc = document.documentElement;
      const pct = Math.round(((window.scrollY + window.innerHeight) / Math.max(doc.scrollHeight, 1)) * 100);
      [25, 50, 75, 100].forEach((m) => {
        if (pct >= (m === 100 ? 98 : m) && !seen.has(m)) {
          seen.add(m);
          track("scroll", String(m));
          if (m === 25) funnel("engaged");
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    const onClick = (e) => {
      const el = e.target.closest?.("button, a, [role='button'], [role='checkbox'], [data-testid]");
      if (!el) return;
      const id = el.getAttribute("data-testid") || (el.textContent || "").trim().slice(0, 40);
      if (id) track("click", id);
    };
    document.addEventListener("click", onClick, true);

    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        const key = en.target.dataset.trackKey;
        if (en.isIntersecting) dwellStart(key); else dwellStop(key);
      });
    }, { threshold: 0.4 });
    const watch = setTimeout(() => {
      SECTIONS.forEach((id) => {
        const el = document.getElementById(id);
        if (el) { el.dataset.trackKey = id; io.observe(el); }
      });
      const footer = document.querySelector("footer");
      if (footer) { footer.dataset.trackKey = "footer"; io.observe(footer); }
    }, 1200);

    return () => {
      clearTimeout(engagedTimer);
      clearTimeout(watch);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("click", onClick, true);
      io.disconnect();
      SECTIONS.concat("footer").forEach(dwellStop);
    };
  }, [consent, pathname]);

  return null;
}
